import React from "react";
import {
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { IUser } from "@utils/interfaces";
import { capitalizeWords } from "@utils/index";
import { KindUser } from "../constants";

const cellSx = {
  backgroundColor: "#FFF",
  border: "none",
  fontSize: "1.4rem",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};
const actionSx = {
  backgroundColor: "#FFF",
  border: "none",
  fontSize: "1.4rem",
  fontWeight: 600,
  color: "#D91F05",
  cursor: "pointer",
  whiteSpace: "normal",
  minWidth: "8rem",
  textAlign: "center",
};
const labelSx = {
  fontSize: "1.1rem",
  fontWeight: 700,
  color: "#726551",
  mb: 0.3,
};
const valueSx = {
  fontSize: "1.3rem",
  color: "#3E3D3D",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};
const seeMoreSx = {
  backgroundColor: "#FFF5F7",
  color: "#D91F05",
  fontWeight: 500,
  fontSize: "1.2rem",
  borderRadius: "50%",
  textAlign: "center" as const,
  ml: 1,
  px: 0.5,
  cursor: "pointer",
  display: "inline-block",
};

interface UserItemProps {
  user: IUser;
  onDetails: () => void;
  onToggleStatus: () => void;
}

const UserItem: React.FC<UserItemProps> = ({
  user,
  onDetails,
  onToggleStatus,
}) => {
  const cnpjCount = user.attributes.CNPJ.length;

  return (
    <>
      {/* Desktop row */}
      <TableRow sx={{ display: { xs: "none", sm: "table-row" } }}>
        <TableCell sx={cellSx}>
          {`${capitalizeWords(user.firstName)} ${capitalizeWords(
            user.lastName
          )}`}
        </TableCell>
        <TableCell sx={cellSx}>{user.email}</TableCell>
        <TableCell sx={{ ...cellSx, maxWidth: 250 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography noWrap sx={{ fontSize: "inherit", maxWidth: 200 }}>
              {capitalizeWords(user.attributes.CNPJ[0]?.companyName || "")}
            </Typography>
            <Tooltip title="Ver mais" placement="top" arrow>
              <Typography
                component="span"
                sx={{ ...seeMoreSx, flexShrink: 0 }}
                onClick={onDetails}
              >
                {cnpjCount <= 1 ? (
                  <i style={{ padding: ".5rem" }}>i</i>
                ) : (
                  `+${cnpjCount - 1}`
                )}
              </Typography>
            </Tooltip>
          </Box>
        </TableCell>
        <TableCell sx={cellSx}>{KindUser[user.kind] ?? user.kind}</TableCell>
        <TableCell align="center" sx={cellSx}>
          -
        </TableCell>
        <TableCell align="center" sx={cellSx}>
          -
        </TableCell>
        <TableCell sx={actionSx} onClick={onToggleStatus}>
          {user.enabled ? "Inativar" : "Ativar"}
        </TableCell>
      </TableRow>

      {/* Mobile card */}
      <Paper
        sx={{
          display: { xs: "block", sm: "none" },
          p: 2,
          mb: 1.5,
          borderRadius: 2,
          boxShadow: "0px 2px 4px rgba(115, 97, 97, 0.10)",
        }}
      >
        <Stack spacing={1}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Typography sx={labelSx}>Nome</Typography>
              <Typography sx={{ ...valueSx, fontWeight: 600 }}>
                {`${capitalizeWords(user.firstName)} ${capitalizeWords(
                  user.lastName
                )}`}
              </Typography>
            </Box>
            <Chip
              label={KindUser[user.kind] ?? user.kind}
              size="small"
              sx={{ fontSize: "1.1rem", textTransform: "capitalize" }}
            />
          </Box>

          <Box>
            <Typography sx={labelSx}>E-mail</Typography>
            <Typography sx={{ ...valueSx, wordBreak: "break-all" }}>
              {user.email}
            </Typography>
          </Box>

          <Box>
            <Typography sx={labelSx}>Razão social</Typography>
            <Typography sx={valueSx}>
              {user.attributes.CNPJ[0]?.companyName ?? "-"}
              {cnpjCount > 1 && (
                <Typography component="span" sx={seeMoreSx} onClick={onDetails}>
                  +{cnpjCount - 1}
                </Typography>
              )}
            </Typography>
          </Box>

          <Box
            sx={{ display: "flex", justifyContent: "space-between", pt: 0.5 }}
          >
            <Button
              size="small"
              onClick={onDetails}
              sx={{ fontSize: "1.2rem" }}
            >
              Detalhes
            </Button>
            <Button
              size="small"
              onClick={onToggleStatus}
              sx={{ color: "#D91F05", fontWeight: 600, fontSize: "1.2rem" }}
            >
              {user.enabled ? "Inativar" : "Ativar"}
            </Button>
          </Box>
        </Stack>
      </Paper>
    </>
  );
};

export default UserItem;
